import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, FileCheck2, ShieldCheck, Info, ArrowRight, RefreshCw } from 'lucide-react';
import { apiFetch, ApiError } from '@/lib/api-client';
import { Modal } from '@/components/ui/Modal';
import { Card } from '@/components/ui/Card';
import { cn } from '@/lib/cn';

// Mirrors the API DocumentCounts / DocumentResult (api types aren't bundled).
interface DocumentCounts {
  totalRecords: number;
  toLog: number;
  alreadyLogged: number;
  excludedRemoved: number;
  excludedRefused: number;
  excludedOutOfScope: number;
}
interface DocumentPreview {
  invoicingMonth: string;
  counts: DocumentCounts;
}
interface DocumentResult extends DocumentCounts {
  invoicingMonth: string;
  appended: number;
  headSeq: string;
}

export function Document() {
  const [preview, setPreview] = useState<DocumentPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<DocumentResult | null>(null);
  // After a successful document action, explain that the Monday/DMP write-back (the
  // other half of step 5) is a PRODUCTION-ONLY link — shown as a modal (Jeremy).
  const [mondayModalOpen, setMondayModalOpen] = useState(false);

  // Guard async setState against an unmount mid-request (consistent with Log.tsx).
  const mounted = useRef(true);
  const confirmBtnRef = useRef<HTMLButtonElement>(null);

  function loadPreview() {
    setLoading(true);
    apiFetch<DocumentPreview>('/document')
      .then((p) => {
        if (mounted.current) {
          setPreview(p);
          setError(null);
        }
      })
      .catch((e) => mounted.current && setError(e instanceof ApiError ? e.message : 'Erreur de chargement'))
      .finally(() => mounted.current && setLoading(false));
  }

  useEffect(() => {
    mounted.current = true;
    loadPreview();
    return () => {
      mounted.current = false;
    };
  }, []);

  // Move focus to the confirmation when it appears (a11y).
  useEffect(() => {
    if (confirming) confirmBtnRef.current?.focus();
  }, [confirming]);

  async function confirmDocument() {
    setBusy(true);
    try {
      const res = await apiFetch<DocumentResult>('/document', { method: 'POST' });
      setResult(res);
      setConfirming(false);
      if (res.appended > 0) {
        toast.success(`${res.appended} enregistrement(s) documenté(s) au journal immuable`);
        setMondayModalOpen(true); // step-5 Monday/DMP write-back = production-only (info modal)
      } else {
        // A concurrent documenter logged them between the preview and the confirm.
        toast.info('Déjà à jour — aucun nouvel enregistrement à documenter');
      }
      loadPreview(); // refresh — the just-logged records now count as "already logged"
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Échec de la documentation');
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <Card className="flex items-center gap-2 p-5 text-sm text-slate-400">
        <Loader2 className="h-4 w-4 animate-spin" /> Préparation de la documentation…
      </Card>
    );
  }
  if (error) {
    return <Card className="p-5 text-sm text-rose-700">Impossible de charger : {error}.</Card>;
  }

  const counts = preview!.counts;
  // Reconcile the headline number Gerd expected (totalRecords) with what is actually
  // written: total = toLog + excluded + alreadyLogged. Surfacing the excluded subtotal
  // makes the "49 → 28" drop obvious instead of leaving it to be inferred.
  const excludedTotal = counts.excludedRemoved + counts.excludedRefused + counts.excludedOutOfScope;

  return (
    <div className="max-w-3xl space-y-5">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Documenter l'éligibilité</h1>
        <p className="mt-1 text-sm text-slate-500">
          Période <span className="font-semibold text-brand-purple">{preview!.invoicingMonth}</span>. Cette action écrit
          un instantané <span className="font-semibold">immuable</span> de chaque enregistrement à facturer dans le
          journal (étape 6).
        </p>
      </div>

      <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <p>
          L'écriture vers <span className="font-semibold">Monday / DMP</span> (l'autre moitié de l'étape 5) reste en
          attente (dépendance DEP-1) : seul le <span className="font-semibold">journal immuable local</span> est écrit
          ici. Le journal est append-only — re-documenter n'ajoute que les nouveaux enregistrements.
        </p>
      </div>

      <Card className="p-5">
        {/* Explicit reconciliation so the total never looks like it "lost" records. */}
        <p className="mb-4 text-sm text-slate-600">
          Sur <span className="font-bold tabular-nums text-slate-900">{counts.totalRecords}</span> enregistrement(s)
          de la période :{' '}
          <span className="font-semibold text-brand-purple">{counts.toLog}</span> à documenter,{' '}
          <span className="font-semibold text-rose-700">{excludedTotal}</span> exclus,{' '}
          <span className="font-semibold text-slate-600">{counts.alreadyLogged}</span> déjà au journal.
        </p>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Stat label="À documenter" value={counts.toLog} tone="purple" big />
          <Stat label="Exclus (non documentés)" value={excludedTotal} tone="rose" big />
          <Stat label="Déjà au journal" value={counts.alreadyLogged} tone="slate" big />
        </div>

        {/* Breakdown of the exclusions so the 21 "missing" records are explained. */}
        {excludedTotal > 0 && (
          <div className="mt-3 rounded-xl border border-rose-100 bg-rose-50/40 p-3">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-rose-700/80">
              Détail des {excludedTotal} exclus
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Stat label="Retirés (REMOVE)" value={counts.excludedRemoved} tone="rose" />
              <Stat label="Refusés (Unifiber)" value={counts.excludedRefused} tone="rose" />
              <Stat label="Hors périmètre" value={counts.excludedOutOfScope} tone="slate" />
            </div>
          </div>
        )}

        <div className="mt-5 border-t border-slate-100 pt-4">
          {counts.toLog === 0 ? (
            <p className="text-sm text-slate-500">
              Rien à documenter pour cette période (tout est déjà au journal ou exclu).
            </p>
          ) : !confirming ? (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              className="flex items-center gap-2 rounded-lg bg-brand-gradient px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90"
            >
              <FileCheck2 className="h-4 w-4" />
              Documenter {counts.toLog} enregistrement(s)
            </button>
          ) : (
            <div
              role="alertdialog"
              aria-label="Confirmer la documentation"
              className="space-y-3 rounded-xl border border-brand-purple/30 bg-brand-purple/5 p-4"
            >
              <p className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                <ShieldCheck className="h-4 w-4 text-brand-purple" />
                Confirmer l'écriture de {counts.toLog} entrée(s) immuable(s) ? Cette action est définitive.
              </p>
              <div className="flex gap-2">
                <button
                  ref={confirmBtnRef}
                  type="button"
                  onClick={confirmDocument}
                  disabled={busy}
                  aria-busy={busy}
                  className="flex items-center gap-2 rounded-lg bg-brand-gradient px-4 py-2 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileCheck2 className="h-4 w-4" />}
                  {busy ? 'Écriture…' : 'Confirmer'}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirming(false)}
                  disabled={busy}
                  className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-60"
                >
                  Annuler
                </button>
              </div>
            </div>
          )}
        </div>
      </Card>

      {result && result.appended > 0 && (
        <Card className="flex items-center justify-between gap-3 border-emerald-200 bg-emerald-50/50 p-4">
          <span className="text-sm text-emerald-800">
            <span className="font-bold">{result.appended}</span> enregistrement(s) écrit(s) au journal immuable.
          </span>
          <Link
            to="/log"
            className="flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-sm font-semibold text-emerald-700 transition-colors hover:bg-emerald-50"
          >
            Ouvrir le journal <ArrowRight className="h-4 w-4" />
          </Link>
        </Card>
      )}

      {/* Step-5 Monday/DMP write-back — production-only (DEP-1). Opens automatically
          after a successful documentation (Jeremy 2026-06-30). */}
      <Modal open={mondayModalOpen} onClose={() => setMondayModalOpen(false)} title="Synchronisation Monday.com">
        <div className="space-y-4 text-sm text-slate-600">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-purple/10 text-brand-purple">
              <RefreshCw className="h-5 w-5" />
            </span>
            <p>
              {result && (
                <>
                  <span className="font-bold text-slate-900">{result.appended}</span> enregistrement(s) viennent d'être
                  écrits dans le journal immuable local.
                </>
              )}
            </p>
          </div>
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800">
            La mise à jour des <span className="font-semibold">4 champs d'éligibilité vers Monday.com</span> (et DMP)
            sera <span className="font-semibold">active uniquement en production</span>. En environnement de test, cette
            synchronisation n'est pas effectuée.
          </div>
          <p className="text-xs text-slate-400">
            Dépendance DEP-1 : board « MDU Management » + compte de service Monday.
          </p>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setMondayModalOpen(false)}
              className="rounded-lg bg-brand-gradient px-4 py-2 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90"
            >
              Compris
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
  big,
}: {
  label: string;
  value: number;
  tone: 'purple' | 'rose' | 'slate';
  big?: boolean;
}) {
  const tones = {
    purple: 'border-brand-purple/30 bg-brand-purple/5 text-brand-purple',
    rose: 'border-rose-200 bg-rose-50 text-rose-700',
    slate: 'border-slate-200 bg-slate-50 text-slate-600',
  };
  return (
    <div className={cn('rounded-xl border px-3 py-2.5', tones[tone])}>
      <div className={cn('font-bold tabular-nums', big ? 'text-2xl' : 'text-lg')}>{value}</div>
      <div className="text-xs font-medium opacity-80">{label}</div>
    </div>
  );
}
